export enum WhoToSplitBetween {
  Everyone = "Everyone",
  SportHouse = "Sport House",
  SportHouseMinusLulu = "Sport House (Without Lulu)",
  BoulderHouse = "Boulder House",
  BoulderHousePlusLulu = "Boulder House (With Lulu)",
  ManualSelection = "Manual Selection",
}

import { FormControl, InputLabel, MenuItem, Select, Typography, TextField, Box, InputAdornment, Divider, Stack, Button } from '@mui/material';
import React, { useEffect, useState } from 'react';
import { AllPeople, BoulderHouse, BoulderHousePlusLulu, SportHouse, SportHouseMinusLulu } from '../../helpers/people';
import { Currency } from '../../helpers/currencies';
import { roundToTwoDecimalPlacesAndFormat } from '../../helpers/money';
import { DatePicker } from '@mui/x-date-pickers/DatePicker'; // Import DatePicker
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns'; // Import Adapter
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider'; // Wrap with LocalizationProvider
import WhosSplittingDialog from './WhosSplittingDialog';
import ExpenseDivisionMethodDialog from './ExpenseDivisionMethodDialog';
import calculateSpreadsheetOutputFromAppInput from '../../helpers/calculateSpreadsheetOutput';
import { addExpense } from '../../helpers/sheets';
import { useSnackbar } from 'notistack';
import { LoadingButton } from '@mui/lab';

export enum ExpenseDivisionMethod {
  Equal = "Equal",
  Manual = "Manual",
}

const WhoToSplitBetweenToLabel: Record<WhoToSplitBetween, string> = {
  [WhoToSplitBetween.Everyone]: "Everyone",
  [WhoToSplitBetween.SportHouse]: "Sport House",
  [WhoToSplitBetween.SportHouseMinusLulu]: "Sport House (Without Lulu)",
  [WhoToSplitBetween.BoulderHousePlusLulu]: "Boulder House (With Lulu)",
  [WhoToSplitBetween.BoulderHouse]: "Boulder House",
  [WhoToSplitBetween.ManualSelection]: "Manual Selection",
}

const ExpenseDivisionMethodToLabel: Record<ExpenseDivisionMethod, string> = {
  [ExpenseDivisionMethod.Equal]: "Equally split",
  [ExpenseDivisionMethod.Manual]: "Unequally split",
}

export const SHARED_PERSON_KEY = "Shared"

export default function AddExpenses() {
  const { enqueueSnackbar } = useSnackbar();

  const [whoPaid, setWhoPaid] = useState<string>();
  const [amount, setAmount] = useState<string | "">("");
  const [currency, setCurrency] = useState<Currency>(Currency.USD);
  const [description, setDescription] = useState<string>("");

  const [WhosSplittingDialogOpen, setWhosSplittingDialogOpen] = useState(false);
  const [whoToSplitBetween, setWhoToSplitBetween] = useState<WhoToSplitBetween>(WhoToSplitBetween.Everyone);
  const [manualSplitListOfPeople, setManualSplitListOfPeople] = useState<string[] | null>(null);

  const [expenseDivisionMethodDialogOpen, setExpenseDivisionMethodDialogOpen] = useState(false);
  const [expenseDivisionMethod, setExpenseDivisionMethod] = useState<ExpenseDivisionMethod>(ExpenseDivisionMethod.Equal);
  const [expenseDivisionPersonToAmountMap, setExpenseDivisionPersonToAmountMap] = useState<Record<string, number>>({});

  // Default to today
  const [date, setDate] = useState<Date | null>(new Date());

  const [submitting, setSubmitting] = useState(false);
  async function submitExpense() {
    try {
      setSubmitting(true);

      const spreadsheetOutput = calculateSpreadsheetOutputFromAppInput(
        whoPaid!,
        date!,
        Number(amount),
        currency,
        description,
        whoToSplitBetween,
        manualSplitListOfPeople || [],
        expenseDivisionMethod,
        expenseDivisionPersonToAmountMap
      )

      const success = await addExpense(spreadsheetOutput!)

      if (success) {
        enqueueSnackbar("Expense added successfully", { variant: "success" });
        reset();
      } else {
        enqueueSnackbar("Failed to add expense, try again or complain to Nat >:(", { variant: "error" });
      }

      setSubmitting(false);
    } catch (error) {
      console.error(error);
      enqueueSnackbar("Failed to add expense, try again or complain to Nat >:(", { variant: "error" });
      setSubmitting(false);
    }
  }

  function listOfPeopleOnThisExpense() {
    switch (whoToSplitBetween) {
      case WhoToSplitBetween.Everyone:
        return AllPeople;
      case WhoToSplitBetween.SportHouse:
        return SportHouse;
      case WhoToSplitBetween.SportHouseMinusLulu:
        return SportHouseMinusLulu;
      case WhoToSplitBetween.BoulderHouse:
        return BoulderHouse;
      case WhoToSplitBetween.BoulderHousePlusLulu:
        return BoulderHousePlusLulu;
      case WhoToSplitBetween.ManualSelection:
        return manualSplitListOfPeople || [];
    }
  }

  function reset() {
    setWhoPaid(undefined);
    setAmount("");
    setCurrency(Currency.USD);
    setDescription("");
    setWhoToSplitBetween(WhoToSplitBetween.Everyone);
    setManualSplitListOfPeople(null);
    setExpenseDivisionMethod(ExpenseDivisionMethod.Equal);
    setDate(new Date());
  }

  function readyToSubmit() {
    return whoPaid &&
      amount && Number(amount) > 0 &&
      currency &&
      description && description.length > 0 &&
      whoToSplitBetween &&
      date;
  }

  function generateSummary() {
    if (!readyToSubmit()) {
      return null;
    }

    return (<Stack direction="column" spacing={2}>
      {/* Who paid what. */}
      <Typography variant="body2">
        <b>{whoPaid}</b> paid <b>${amount} {currency}</b> for <b>{description}</b>.
      </Typography>

      {/* Who is responsible for repaying? */}
      <Typography variant="body2">
        {
          whoToSplitBetween === WhoToSplitBetween.Everyone ? (
            <>This expense will be split between <b>everyone on the trip</b>.</>
          ) : whoToSplitBetween === WhoToSplitBetween.SportHouse ? (
            <>This expense will be split between the <b>sport climbing house</b>.</>
          ) : whoToSplitBetween === WhoToSplitBetween.BoulderHouse ? (
            <>This expense will be split between the <b>bouldering house</b>.</>
          ) : whoToSplitBetween === WhoToSplitBetween.SportHouseMinusLulu ? (
            <>This expense will be split between the <b>sport climbing house</b> minus Lulu.</>
          ) : whoToSplitBetween === WhoToSplitBetween.BoulderHousePlusLulu ? (
            <>This expense will be split between the <b>bouldering house</b> with Lulu.</>
          ) : (
            <>This expense will be split between <b> {manualSplitListOfPeople!.join(", ")}</b>.</>
          )
        }
      </Typography>

      {/* How is the expense divided? */}
      <Typography variant="body2">
        {
          expenseDivisionMethod === ExpenseDivisionMethod.Equal ? (
            <>This expense will be split <b>equally among everyone</b>.</>
          ) : (
            <>Each person is responsible for a <b>certain specified amount</b>.</>
          )
        }
      </Typography>
    </Stack>)
  }

  return (
    <>
      <LocalizationProvider dateAdapter={AdapterDateFns}>
        <Stack direction="column" spacing={2} height="100%">
          <Typography variant="h6" gutterBottom>
            Add an expense
          </Typography>
          <Stack direction="row" spacing={2}>
            <FormControl sx={{ flexGrow: 1 }}>
              <InputLabel>Who paid?</InputLabel>
              <Select
                value={whoPaid || ""}
                label="Who paid?"
                onChange={(e) => setWhoPaid(e.target.value)}
                MenuProps={{
                  PaperProps: {
                    style: {
                      maxHeight: 400,
                    },
                  },
                }}
              >
                {AllPeople.map((person) => (
                  <MenuItem key={person} value={person}>
                    {person}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {/* Date Picker */}

            <Box sx={{ width: "calc(50% - 4px)" }}>
              <DatePicker
                label="Date"
                value={date}
                onChange={(newDate) => setDate(newDate)}
                format="MMMM d, yyyy"
                sx={{ width: "100%" }}
              />
            </Box>
          </Stack>

          {/* Amount entry with a currency dropdown */}
          <Box display="flex" alignItems="center">
            <TextField
              label="Amount"
              type="number"
              required
              fullWidth
              value={amount}
              placeholder="0.00"
              onChange={(e) => setAmount(e.target.value)} // Let the user type freely
              onBlur={(e) => {
                if (e.target.value !== "") {
                  setAmount(roundToTwoDecimalPlacesAndFormat(Number(e.target.value))); // Format only on blur
                }
              }}
              inputProps={{ min: 0 }}
              InputProps={{
                startAdornment: <InputAdornment position="start">
                  {currency == Currency.USD ? "$" : "C$"}
                </InputAdornment>,
              }}
            />
            <FormControl sx={{ minWidth: 100, ml: 2 }}>
              <InputLabel>Currency</InputLabel>
              <Select
                value={currency}
                label="Currency"
                onChange={(e) => setCurrency(e.target.value as Currency)}
              >
                {Object.keys(Currency).map((curr) => (
                  <MenuItem key={curr} value={curr}>
                    {curr}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>

          {/* Description field */}
          <TextField
            label="Description"
            required
            fullWidth
            multiline
            minRows={1} // Default to a single row
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="Enter a description"
            sx={{ mt: 2 }}
          />

          <Divider />

          <Stack direction="row" spacing={2} alignItems="center">
            <Typography variant="body1"><b>Who's splitting this expense?</b></Typography>
            <Box flexGrow={1} />
            <Button
              variant="outlined"
              color="primary"
              sx={{
                width: "fit-content",
                whiteSpace: "nowrap",
              }}
              onClick={() => setWhosSplittingDialogOpen(true)}
            >
              {WhoToSplitBetweenToLabel[whoToSplitBetween]}
            </Button>
          </Stack>
          <Divider />

          <Stack direction="row" spacing={2} alignItems="center">
            <Typography variant="body1"><b>How is this expense being split?</b></Typography>
            <Box flexGrow={1} />
            <Button
              variant="outlined"
              color="primary"

              onClick={() => setExpenseDivisionMethodDialogOpen(true)}
              sx={{
                width: "fit-content",
                whiteSpace: "nowrap",
              }}
            >
              {ExpenseDivisionMethodToLabel[expenseDivisionMethod]}
            </Button>
          </Stack>

          <Divider />
          <Typography variant="body1"><b>Summary</b></Typography>
          <Box>
            <Box ml={2}>
              {
                readyToSubmit() ? (
                  generateSummary()
                ) : (
                  <Typography variant="body2" color="text.secondary">Please fill out all fields</Typography>
                )
              }
            </Box>
          </Box>
          <Box flexGrow={1} />
          <Stack direction="row" spacing={2}>
            <Box flexGrow={1} />
            <Button variant="outlined" onClick={reset}>Reset</Button>
            <LoadingButton
              variant="contained"
              disabled={!readyToSubmit()}
              loading={submitting}
              onClick={submitExpense}
              sx={{
                width: "150px"
              }}
            >Submit</LoadingButton>
          </Stack>
        </Stack>
      </LocalizationProvider>

      <WhosSplittingDialog
        open={WhosSplittingDialogOpen}
        onClose={() => setWhosSplittingDialogOpen(false)}
        whoToSplitBetween={whoToSplitBetween}
        manualSplitListOfPeople={manualSplitListOfPeople}
        onConfirm={(whoToSplitBetween, manualSplitListOfPeople) => {
          setWhoToSplitBetween(whoToSplitBetween);
          setManualSplitListOfPeople(manualSplitListOfPeople);
          setWhosSplittingDialogOpen(false);
        }}
      />

      <ExpenseDivisionMethodDialog
        open={expenseDivisionMethodDialogOpen}
        expenseTotal={Number(amount)}
        peopleOnThisExpense={listOfPeopleOnThisExpense()}
        onClose={() => setExpenseDivisionMethodDialogOpen(false)}
        expenseDivisionMethod={expenseDivisionMethod}
        expenseDivisionPersonToAmountMap={expenseDivisionPersonToAmountMap}
        onSubmit={(expenseDivisionMethod, expenseDivisionPersonToAmountMap) => {
          setExpenseDivisionMethod(expenseDivisionMethod);
          setExpenseDivisionPersonToAmountMap(expenseDivisionPersonToAmountMap);
          setExpenseDivisionMethodDialogOpen(false);
        }}
      />
    </>
  );
}
